import React, { useEffect, useState } from 'react'
import { Card, Typography, Button, Input, Select, Modal, message, Space, Spin, Divider } from 'antd'
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons'
import { wellbeingAPI } from '../services/api'

const { Title, Text } = Typography

const MOODS = [
  { key: 'energia', label: 'Con mucha energía', emoji: '⚡' },
  { key: 'bien', label: 'Bien', emoji: '🙂' },
  { key: 'masomenos', label: 'Más o menos', emoji: '😐' },
  { key: 'bajoneada', label: 'Bajoneada', emoji: '😔' },
  { key: 'cansada', label: 'Cansada', emoji: '😴' }
]

const WEEKDAYS = [
  { value: 'all', label: 'Todos los días' },
  { value: 'monday', label: 'Lunes' },
  { value: 'tuesday', label: 'Martes' },
  { value: 'wednesday', label: 'Miércoles' },
  { value: 'thursday', label: 'Jueves' },
  { value: 'friday', label: 'Viernes' }
]

const GOLD = '#e0b34a'

function Bienestar() {
  const [moods, setMoods] = useState(null)
  const [stats, setStats] = useState([])
  const [habits, setHabits] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(null)
  const [title, setTitle] = useState('')
  const [emoji, setEmoji] = useState('✅')
  const [weekday, setWeekday] = useState('all')

  const load = async () => {
    setLoading(true)
    try {
      const [m, s, h] = await Promise.all([wellbeingAPI.getMoodsToday(), wellbeingAPI.getTodayStats(), wellbeingAPI.getHabits()])
      setMoods(m.data); setStats(s.data || []); setHabits(h.data || [])
    } catch (e) { message.error('Error al cargar los datos') }
    finally { setLoading(false) }
  }

  useEffect(() => { load() }, [])

  const openCreate = (wd) => { setTitle(''); setEmoji('✅'); setWeekday(wd || 'all'); setModal({ weekday: wd || 'all' }) }
  const openEdit = (h) => { setTitle(h.title); setEmoji(h.emoji || '✅'); setWeekday(h.weekday); setModal({ habit: h }) }
  const save = async () => {
    if (!title.trim()) return message.warning('Escribí el nombre del hábito')
    try {
      if (modal?.habit) await wellbeingAPI.updateHabit(modal.habit.id, { title: title.trim(), emoji, weekday })
      else await wellbeingAPI.createHabit({ title: title.trim(), emoji, weekday })
      message.success('Hábito guardado')
      setModal(null); load()
    } catch (e) { message.error(e.response?.data?.error || 'Error al guardar') }
  }
  const removeHabit = (h) => {
    Modal.confirm({
      title: 'Eliminar hábito',
      content: `¿Eliminar "${h.title}"? Las completaciones de hoy no se borran.`,
      okText: 'Eliminar',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: async () => {
        try { await wellbeingAPI.removeHabit(h.id); message.success('Hábito eliminado'); load() }
        catch (e) { message.error('Error al eliminar') }
      }
    })
  }

  const totalMoods = moods ? Object.values(moods.counts || {}).reduce((a, b) => a + b, 0) : 0

  return (
    <div>
      <Title level={3} style={{ color: '#fff', marginTop: 0 }}>Bienestar de mis alumnas</Title>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 16 }}>
        {MOODS.map(m => (
          <Card key={m.key} style={{ background: '#141414', border: '1px solid #222', borderRadius: 16, textAlign: 'center' }}>
            <Text style={{ fontSize: 30, display: 'block' }}>{m.emoji}</Text>
            <Text style={{ color: GOLD, fontSize: 28, fontWeight: 800, display: 'block' }}>{moods?.counts?.[m.key] ?? 0}</Text>
            <Text style={{ color: '#888', fontSize: 12, display: 'block', marginTop: 2 }}>{m.label}</Text>
          </Card>
        ))}
        <Card style={{ background: '#141414', border: '1px solid #222', borderRadius: 16, textAlign: 'center' }}>
          <Text style={{ fontSize: 30, display: 'block' }}>💛</Text>
          <Text style={{ color: '#fff', fontSize: 28, fontWeight: 800, display: 'block' }}>{totalMoods}</Text>
          <Text style={{ color: '#888', fontSize: 12, display: 'block', marginTop: 2 }}>Total hoy</Text>
        </Card>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16, alignItems: 'start' }}>
        <Card title={<Text style={{ color: '#fff', fontWeight: 700 }}>Mensajes de las alumnas (hoy)</Text>} style={{ background: '#141414', border: '1px solid #222', borderRadius: 16 }}>
          {loading ? <Spin /> : !moods ? <Text style={{ color: '#666' }}>Sin datos</Text> : moods.comments.length === 0 ? (
            <Text style={{ color: '#555', fontSize: 13 }}>Hoy todavía no hay mensajes. Las alumnas pueden contarte cómo se sienten desde el portal.</Text>
          ) : moods.comments.map((c, i) => (
            <div key={i} style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: '1px solid #1a1a1a' }}>
              <Text style={{ fontSize: 24 }}>{MOODS.find(m => m.key === c.mood)?.emoji || '💛'}</Text>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <Text style={{ color: '#fff', fontSize: 13, fontWeight: 700 }}>{c.name} {c.lastName}</Text>
                  <Text style={{ color: '#555', fontSize: 10 }}>{new Date(c.createdAt).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}</Text>
                </div>
                <Text style={{ color: '#aaa', fontSize: 13, display: 'block' }}>{c.comment || '(solo eligió la emoción)'}</Text>
              </div>
            </div>
          ))}
        </Card>

        <Card title={<Text style={{ color: '#fff', fontWeight: 700 }}>Progreso de hoy (Mi día)</Text>} style={{ background: '#141414', border: '1px solid #222', borderRadius: 16 }}>
          {loading ? <Spin /> : stats.length === 0 ? (
            <Text style={{ color: '#555', fontSize: 13 }}>No hay hábitos cargados para hoy o no hay alumnas activas.</Text>
          ) : stats.map((s, i) => (
            <div key={s.clientId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #1a1a1a' }}>
              <Text style={{ color: '#fff', fontSize: 14 }}>{s.name} {s.lastName}</Text>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 70, height: 6, borderRadius: 4, background: '#1c1c1c', overflow: 'hidden' }}>
                  <div style={{ width: `${s.total ? Math.round(s.done / s.total * 100) : 0}%`, height: '100%', background: GOLD }} />
                </div>
                <Text style={{ color: '#fff', fontSize: 13, fontWeight: 700 }}>{s.done}/{s.total}</Text>
              </div>
            </div>
          ))}
        </Card>
      </div>

      <Card title={<Text style={{ color: '#fff', fontWeight: 700 }}>Hábitos de "Mi día"</Text>} style={{ background: '#141414', border: '1px solid #222', borderRadius: 16, marginTop: 16 }}>
        <Text style={{ color: '#888', fontSize: 12, display: 'block', marginBottom: 12 }}>
          Definí los hábitos que ven las alumnas. Podés configurar distintos por día de la semana (Lunes a Viernes) o dejarlos para todos los días.
        </Text>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
          {WEEKDAYS.map(w => {
            const list = habits.filter(h => h.weekday === w.value)
            return (
              <div key={w.value} style={{ background: '#1c1c1c', border: '1px solid #2a2a2a', borderRadius: 12, padding: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <Text style={{ color: '#fff', fontSize: 13, fontWeight: 700 }}>{w.label}</Text>
                  <Button size="small" type="link" icon={<PlusOutlined />} style={{ color: GOLD, fontWeight: 700 }} onClick={() => openCreate(w.value)}>Agregar</Button>
                </div>
                {list.length === 0 ? <Text style={{ color: '#555', fontSize: 12 }}>Sin hábitos</Text> : list.map(h => (
                  <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #222' }}>
                    <Text style={{ fontSize: 16 }}>{h.emoji || '✅'}</Text>
                    <Text style={{ color: '#ddd', fontSize: 13, flex: 1 }}>{h.title}</Text>
                    <EditOutlined style={{ color: '#888', cursor: 'pointer', fontSize: 12 }} onClick={() => openEdit(h)} />
                    <DeleteOutlined style={{ color: '#ff4d4f', cursor: 'pointer', fontSize: 12 }} onClick={() => removeHabit(h)} />
                  </div>
                ))}
              </div>
            )
          })}
        </div>
      </Card>

      <Modal open={!!modal} onCancel={() => setModal(null)} onOk={save} okText="Guardar" cancelText="Cancelar" title={modal?.habit ? 'Editar hábito' : 'Nuevo hábito'}>
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          <Input placeholder="Emoji (ej: 💧)" value={emoji} onChange={(e) => setEmoji(e.target.value)} maxLength={4} />
          <Input placeholder="Nombre del hábito (ej: Tomar suficiente agua)" value={title} onChange={(e) => setTitle(e.target.value)} />
          <Select style={{ width: '100%' }} value={weekday} onChange={setWeekday} options={WEEKDAYS} />
        </Space>
      </Modal>
    </div>
  )
}

export default Bienestar
